import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { assignDeviceTags, unassignDeviceTags } from "@/services/tagAssignmentApi";
import { getCurrentUserEmail } from "@/lib/auth";

interface Tag {
  id: string;
  name: string;
  description?: string;
  color: string;
  type: string;
}

export const useTagAssignment = (
  resourceType: "device" | "profile" | "application" | "configuration" | "patch_policy" | "user"
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const email = getCurrentUserEmail() || 'unknown';

  const assignTag = useCallback(async (resourceId: string, tagId: string) => {
    setIsLoading(true);
    try {
      await assignDeviceTags({ tag_ids: [tagId], device_ids: [resourceId], created_by: email, updated_by: email });
      toast({ title: "Tag assigned", description: "Tag has been successfully assigned." });
      return true;
    } catch (error) {
      console.error("Error assigning tag:", error);
      toast({ title: "Error", description: "Failed to assign tag.", variant: "destructive" });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [resourceType, toast, email]);

  const removeTag = useCallback(async (resourceId: string, tagId: string) => {
    setIsLoading(true);
    try {
      await unassignDeviceTags({ tag_ids: [tagId], device_id: resourceId, created_by: email, updated_by: email });
      toast({ title: "Tag removed", description: "Tag has been successfully removed." });
      return true;
    } catch (error) {
      console.error("Error removing tag:", error);
      toast({ title: "Error", description: "Failed to remove tag.", variant: "destructive" });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [resourceType, toast, email]);

  const getAssignedTags = useCallback(async (_resourceId: string): Promise<Tag[]> => {
    return [];
  }, []);

  const bulkAssignTags = useCallback(async (resourceId: string, tagIds: string[]) => {
    setIsLoading(true);
    try {
      await assignDeviceTags({ tag_ids: tagIds, device_ids: [resourceId], created_by: email, updated_by: email });
      toast({ title: "Tags assigned", description: `${tagIds.length} tag(s) assigned.` });
      return true;
    } catch (error) {
      console.error("Error bulk assigning tags:", error);
      toast({ title: "Error", description: "Failed to assign tags.", variant: "destructive" });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [resourceType, toast, email]);

  return { assignTag, removeTag, getAssignedTags, bulkAssignTags, isLoading };
};
